import { MILLI_PER_FLOOR } from './constants.js';
import { gradedValue, starsFor } from './rules/objective.js';
import type { GameState, RunStatus, StarMetric } from './types.js';

/**
 * A flat, presentation-ready view of how a run went.
 *
 * Every field here has to be explainable in one line on the results screen —
 * "readable chaos" means the player can always answer *why* they got this score.
 */
export interface RunSummary {
  readonly status: RunStatus;
  readonly stars: 0 | 1 | 2 | 3;
  readonly delivered: number;
  readonly totalPassengers: number;
  readonly elapsedMs: number;
  /** Spawn to boarding, averaged over everyone who has boarded. */
  readonly avgWaitMs: number;
  /** Boarding to arrival, averaged over everyone delivered. */
  readonly avgRideMs: number;
  /** Floors travelled by the car. The route-efficiency proxy. */
  readonly floorsTravelled: number;
  readonly doorCycles: number;
  readonly endReason: string | null;
  /** What this level grades on, so the results screen leads with the right number. */
  readonly metric: StarMetric;
  /** The number it grades: the score, the time, or the trips (ADR-0037). */
  readonly graded: number;
  /** Each time a car set off for a floor. */
  readonly trips: number;
  readonly service: number;
  readonly abandoned: number;
  readonly couriersOnTime: number;
  readonly couriersLate: number;
  readonly vipsServed: number;
  /** First legs completed at an interchange. Zero in a building without one. */
  readonly transfers: number;
  /** Turned away because the landing was full. Zero where corridors are wide. */
  readonly turnedAway: number;
  /** Which traits this level actually used, so the breakdown hides irrelevant rows. */
  readonly archetypes: readonly string[];
}

export function summarize(state: GameState): RunSummary {
  const boarded = state.passengers.filter((p) => p.boardedMs !== null).length;
  const {
    delivered,
    totalWaitMs,
    totalRideMs,
    distanceMilli,
    doorCycles,
    service,
    abandoned,
    couriersOnTime,
    couriersLate,
    vipsServed,
    transfers,
  } = state.score;

  const archetypes = [...new Set(state.passengers.map((p) => p.archetype))].filter(
    (a) => a !== 'normal',
  );

  return {
    status: state.status,
    stars: starsFor(state),
    delivered,
    totalPassengers: state.passengers.length,
    elapsedMs: state.elapsedMs,
    avgWaitMs: boarded === 0 ? 0 : Math.round(totalWaitMs / boarded),
    avgRideMs: delivered === 0 ? 0 : Math.round(totalRideMs / delivered),
    floorsTravelled: Math.round((distanceMilli / MILLI_PER_FLOOR) * 100) / 100,
    doorCycles,
    endReason: state.endReason,
    metric: state.stars.metric ?? 'time',
    graded: gradedValue(state),
    trips: state.score.trips,
    service: Math.round(service),
    abandoned,
    couriersOnTime,
    couriersLate,
    vipsServed,
    transfers,
    turnedAway: state.passengers.filter((p) => p.lostReason === 'no-room').length,
    archetypes,
  };
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`
    : `${seconds}.${tenths}s`;
}
